import { Directive, ElementRef, AfterViewInit, OnDestroy, Input } from '@angular/core';

@Directive({
  selector: '[appResizable]',
  standalone: false
})
export class ResizableDirective implements AfterViewInit, OnDestroy {
  @Input() minWidth: number = 200;
  @Input() minHeight: number = 150;
  @Input() onResize?: () => void;
  @Input() useViewportUnits: boolean = true; // Use vw/vh instead of px

  private isResizing = false;
  private initialX = 0;
  private initialY = 0;
  private initialWidth = 0;
  private initialHeight = 0;
  private initialWidthVw = 0;
  private initialHeightVh = 0;
  private currentResizer: HTMLElement | null = null;
  private viewportWidth = 0;
  private viewportHeight = 0;

  private mouseMoveHandler = this.onMouseMove.bind(this);
  private mouseUpHandler = this.onMouseUp.bind(this);
  private touchMoveHandler = this.onTouchMove.bind(this);
  private touchEndHandler = this.onTouchEnd.bind(this);
  private windowResizeHandler = this.updateViewportDimensions.bind(this);
  private baseEl!:  | any;


  constructor(private el: ElementRef<HTMLElement>) {
    // Find the target element - could be popup-panel or modal-content
    this.baseEl = this.el.nativeElement.closest('.popup-panel') || 
                  this.el.nativeElement.closest('.modal-content') ||
                  this.el.nativeElement;
  }

  ngAfterViewInit(): void {
    // Ensure we have the correct element reference
    if (!this.baseEl || this.baseEl === this.el.nativeElement) {
      this.baseEl = this.el.nativeElement.closest('.popup-panel') || 
                    this.el.nativeElement.closest('.modal-content') ||
                    this.el.nativeElement;
    }
    // Initialize viewport dimensions
    this.updateViewportDimensions();
    // Update viewport dimensions on window resize
    window.addEventListener('resize', this.windowResizeHandler);
    this.setupResizers();
  }

  private updateViewportDimensions(): void {
    this.viewportWidth = window.innerWidth;
    this.viewportHeight = window.innerHeight;
  }

  ngOnDestroy(): void {
    document.removeEventListener('mousemove', this.mouseMoveHandler);
    document.removeEventListener('mouseup', this.mouseUpHandler);
    document.removeEventListener('touchmove', this.touchMoveHandler);
    document.removeEventListener('touchend', this.touchEndHandler);
    window.removeEventListener('resize', this.windowResizeHandler);
  }

  private setupResizers(): void {
    const resizers = this.el.nativeElement.querySelectorAll('.resizer');
    
    resizers.forEach((resizer: Element) => {
      const resizerEl = resizer as HTMLElement;
      resizerEl.addEventListener('mousedown', (e: Event) => this.onMouseDown(e as MouseEvent, resizerEl));
      // Touch support for mobile devices
      resizerEl.addEventListener('touchstart', (e: Event) => {
        const touchEvent = (e as TouchEvent).touches[0];
        const mouseEvent = new MouseEvent('mousedown', {
          clientX: touchEvent.clientX,
          clientY: touchEvent.clientY,
          bubbles: true,
          cancelable: true
        });
        this.onMouseDown(mouseEvent, resizerEl);
        e.preventDefault();
      });
    });

    document.addEventListener('mousemove', this.mouseMoveHandler);
    document.addEventListener('mouseup', this.mouseUpHandler);
    document.addEventListener('touchmove', this.touchMoveHandler);
    document.addEventListener('touchend', this.touchEndHandler);
  }

  private onTouchMove(e: TouchEvent): void {
    if (this.isResizing && e.touches[0]) {
      const touch = e.touches[0];
      const mouseEvent = new MouseEvent('mousemove', {
        clientX: touch.clientX,
        clientY: touch.clientY,
        bubbles: true,
        cancelable: true
      });
      this.onMouseMove(mouseEvent);
      e.preventDefault();
    }
  }

  private onTouchEnd(): void {
    this.onMouseUp();
  }

  private onMouseDown(e: MouseEvent, resizer: HTMLElement): void {
    // Ensure we have the correct element reference
    if (!this.baseEl || this.baseEl === this.el.nativeElement) {
      this.baseEl = this.el.nativeElement.closest('.popup-panel') || 
                    this.el.nativeElement.closest('.modal-content') ||
                    this.el.nativeElement;
    }
    
    // Check if the element is maximized - if so, don't allow resizing
    const element = this.baseEl as HTMLElement;
    if (element && (element.style.width === '100%' || element.classList.contains('maximized'))) {
      return;
    }
    
    // Update viewport dimensions
    this.updateViewportDimensions();
    
    this.isResizing = true;
    this.currentResizer = resizer;
    this.initialX = e.clientX;
    this.initialY = e.clientY;
    this.initialWidth = element?.offsetWidth || 0;
    this.initialHeight = element?.offsetHeight || 0;
    
    // Calculate initial dimensions as viewport percentages
    if (this.useViewportUnits && this.viewportWidth > 0 && this.viewportHeight > 0) {
      this.initialWidthVw = (this.initialWidth / this.viewportWidth) * 100;
      this.initialHeightVh = (this.initialHeight / this.viewportHeight) * 100;
    }
    
    e.preventDefault();
    e.stopPropagation();
  }

  private onMouseMove(e: MouseEvent): void {
    if (!this.isResizing || !this.currentResizer || !this.baseEl) return;

    const deltaX = e.clientX - this.initialX;
    const deltaY = e.clientY - this.initialY;
    const element = this.baseEl as HTMLElement;

    // Calculate minimum dimensions as viewport percentages
    const minWidthVw = this.viewportWidth > 0 ? (this.minWidth / this.viewportWidth) * 100 : 0;
    const minHeightVh = this.viewportHeight > 0 ? (this.minHeight / this.viewportHeight) * 100 : 0;

    if (this.useViewportUnits && this.viewportWidth > 0 && this.viewportHeight > 0) {
      // Calculate deltas as viewport percentages
      const deltaXVw = (deltaX / this.viewportWidth) * 100;
      const deltaYVh = (deltaY / this.viewportHeight) * 100;

      // Handle different resizer positions using viewport units
      if (this.currentResizer.classList.contains('bottom-right')) {
        const newWidthVw = Math.max(minWidthVw, this.initialWidthVw + deltaXVw);
        const newHeightVh = Math.max(minHeightVh, this.initialHeightVh + deltaYVh);
        element.style.width = newWidthVw + 'vw';
        element.style.height = newHeightVh + 'vh';
      } else if (this.currentResizer.classList.contains('bottom-left')) {
        const newWidthVw = Math.max(minWidthVw, this.initialWidthVw - deltaXVw);
        const newHeightVh = Math.max(minHeightVh, this.initialHeightVh + deltaYVh);
        element.style.width = newWidthVw + 'vw';
        element.style.height = newHeightVh + 'vh';
      } else if (this.currentResizer.classList.contains('top-right')) {
        const newWidthVw = Math.max(minWidthVw, this.initialWidthVw + deltaXVw);
        const newHeightVh = Math.max(minHeightVh, this.initialHeightVh - deltaYVh);
        element.style.width = newWidthVw + 'vw';
        element.style.height = newHeightVh + 'vh';
      } else if (this.currentResizer.classList.contains('top-left')) {
        const newWidthVw = Math.max(minWidthVw, this.initialWidthVw - deltaXVw);
        const newHeightVh = Math.max(minHeightVh, this.initialHeightVh - deltaYVh);
        element.style.width = newWidthVw + 'vw';
        element.style.height = newHeightVh + 'vh';
      } else if (this.currentResizer.classList.contains('right')) {
        const newWidthVw = Math.max(minWidthVw, this.initialWidthVw + deltaXVw);
        element.style.width = newWidthVw + 'vw';
      } else if (this.currentResizer.classList.contains('left')) {
        const newWidthVw = Math.max(minWidthVw, this.initialWidthVw - deltaXVw);
        element.style.width = newWidthVw + 'vw';
      } else if (this.currentResizer.classList.contains('bottom')) {
        const newHeightVh = Math.max(minHeightVh, this.initialHeightVh + deltaYVh);
        element.style.height = newHeightVh + 'vh';
      } else if (this.currentResizer.classList.contains('top')) {
        const newHeightVh = Math.max(minHeightVh, this.initialHeightVh - deltaYVh);
        element.style.height = newHeightVh + 'vh';
      }
    } else {
      // Fallback to pixel-based resizing
      if (this.currentResizer.classList.contains('bottom-right')) {
        element.style.width = Math.max(this.minWidth, this.initialWidth + deltaX) + 'px';
        element.style.height = Math.max(this.minHeight, this.initialHeight + deltaY) + 'px';
      } else if (this.currentResizer.classList.contains('bottom-left')) {
        element.style.width = Math.max(this.minWidth, this.initialWidth - deltaX) + 'px';
        element.style.height = Math.max(this.minHeight, this.initialHeight + deltaY) + 'px';
      } else if (this.currentResizer.classList.contains('top-right')) {
        element.style.width = Math.max(this.minWidth, this.initialWidth + deltaX) + 'px';
        element.style.height = Math.max(this.minHeight, this.initialHeight - deltaY) + 'px';
      } else if (this.currentResizer.classList.contains('top-left')) {
        element.style.width = Math.max(this.minWidth, this.initialWidth - deltaX) + 'px';
        element.style.height = Math.max(this.minHeight, this.initialHeight - deltaY) + 'px';
      } else if (this.currentResizer.classList.contains('right')) {
        element.style.width = Math.max(this.minWidth, this.initialWidth + deltaX) + 'px';
      } else if (this.currentResizer.classList.contains('left')) {
        element.style.width = Math.max(this.minWidth, this.initialWidth - deltaX) + 'px';
      } else if (this.currentResizer.classList.contains('bottom')) {
        element.style.height = Math.max(this.minHeight, this.initialHeight + deltaY) + 'px';
      } else if (this.currentResizer.classList.contains('top')) {
        element.style.height = Math.max(this.minHeight, this.initialHeight - deltaY) + 'px';
      }
    }

    // Call optional resize callback
    if (this.onResize) {
      this.onResize();
    }
  }

  private onMouseUp(): void {
    this.isResizing = false;
    this.currentResizer = null;
  }
}

